/**
 * Canonical serialization + SHA-256 signature for DecisionRecords.
 *
 * v1 signature is a content-hash, not a cryptographic signature —
 * tamper-evident, not tamper-proof. v2 will use real signing once
 * the auth layer supports it. See the study brief's "Out of scope"
 * section.
 *
 * The signature covers the record body EXCLUDING the signature field
 * itself, so re-signing is idempotent.
 *
 * @module gibbs/decision-records/signature
 */

/**
 * Produce a deterministic JSON representation of an object: keys
 * sorted at every level, arrays preserved in order. The signature
 * is computed over this canonical form so byte-identical input
 * always produces byte-identical output.
 *
 * @param {*} value
 * @returns {string}
 */
export function canonicalize(value) {
    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map(canonicalize).join(',')}]`;
    }
    const keys = Object.keys(value).sort();
    const parts = keys.map(
        (k) => `${JSON.stringify(k)}:${canonicalize(value[k])}`
    );
    return `{${parts.join(',')}}`;
}

/**
 * Compute a SHA-256 hash of a string. Uses globalThis.crypto.subtle
 * when available (modern browsers + Node 19+); falls back to
 * node:crypto in test / older Node environments.
 *
 * @param {string} text
 * @returns {Promise<string>} hex-encoded digest
 */
async function sha256(text) {
    // Browser / Node 19+ path
    if (typeof globalThis !== 'undefined'
        && globalThis.crypto?.subtle?.digest) {
        const enc = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
        const buf = enc ? enc.encode(text) : Buffer.from(text, 'utf8');
        const digest = await globalThis.crypto.subtle.digest('SHA-256', buf);
        return bufferToHex(new Uint8Array(digest));
    }
    // Older Node fallback — synchronous require to avoid bundlers
    // following the path in browser builds.
    // eslint-disable-next-line global-require, import/no-nodejs-modules
    const nodeCrypto = require('crypto');
    return nodeCrypto.createHash('sha256').update(text, 'utf8').digest('hex');
}

function bufferToHex(uint8) {
    let hex = '';
    for (let i = 0; i < uint8.length; i += 1) {
        const b = uint8[i].toString(16);
        hex += b.length === 1 ? `0${b}` : b;
    }
    return hex;
}

/**
 * Compute the signature for a DecisionRecord. The signature field
 * itself is excluded from the hash input so re-signing is idempotent.
 *
 * @param {Object} record
 * @returns {Promise<string>} hex-encoded SHA-256
 */
export async function signRecord(record) {
    if (!record || typeof record !== 'object') {
        throw new Error('signRecord requires a record object');
    }
    const { signature: _ignored, ...body } = record;
    return sha256(canonicalize(body));
}

/**
 * Verify a record's signature matches its content.
 *
 * @param {Object} record
 * @returns {Promise<boolean>}
 */
export async function verifySignature(record) {
    if (!record || !record.signature) return false;
    const expected = await signRecord(record);
    return expected === record.signature;
}
